import "Mention/Scripts/TFS.Mention.WorkItems.Registration"; // to register work-item mention parser and provider
import * as Controls from "VSS/Controls";
import * as Utils_String from "VSS/Utils/String";
import { MentionProcessor } from "Mention/Scripts/TFS.Mention";
import { IAutocompletePluginOptions, MentionType } from "Mention/Scripts/TFS.Mention.Autocomplete";
import { AutocompleteEnhancement, IAutocompleteOptions, IAutocompletePluginConfig } from "Mention/Scripts/TFS.Mention.Autocomplete.Controls";
import { PersonAutocompleteProvider } from "Mention/Scripts/TFS.Mention.People";
import { WorkItemAutocompleteProvider } from "Mention/Scripts/TFS.Mention.WorkItems";

interface Point {
    x: number;
    y: number;
}

export class MentionNwpWrapper {
    private _mentionEnhancement: Controls.Enhancement<IAutocompleteOptions>;
    private _textControl: HTMLTextAreaElement;

    public constructor(
        textArea: HTMLTextAreaElement,
        onSelect: () => void,
        private _textEditorMirrorContainerRef: HTMLDivElement,
        private _textEditorMirrorRef: HTMLSpanElement,
        private _positioningElementRef: HTMLDivElement
    ) {
        this._textControl = textArea;
        const autoCompleteOptions = this._getAutoCompleteOptions(onSelect);

        this._mentionEnhancement = Controls.Enhancement.enhance(AutocompleteEnhancement, $(textArea), autoCompleteOptions);
    }

    public dispose(): void {
        this._mentionEnhancement.dispose();
        this._mentionEnhancement = null;
    }

    private _getAutoCompleteOptions(onSelect: () => void): IAutocompleteOptions {
        const pluginConfigs: IAutocompletePluginConfig<IAutocompletePluginOptions>[] = [];

        pluginConfigs.push({
            factory: (options: IAutocompletePluginOptions) => {
                options.positioningElement = this._getPositioningWIElement;
                return new WorkItemAutocompleteProvider(options);
            }
        });

        pluginConfigs.push({
            factory: (options: IAutocompletePluginOptions) => {
                options.positioningElement = this._getPositioningPeopleElement;
                return new PersonAutocompleteProvider(options);
            }
        });

        return {
            mentionType: MentionType.WorkItem,
            pluginConfigs: pluginConfigs,
            select: onSelect,
            useBodyAsContainer: true
        };
    }

    private _getPositioningWIElement = (): JQuery => {
        return this._getPositioningElementForLastIndexOf("#");
    };

    private _getPositioningPeopleElement = (): JQuery => {
        return this._getPositioningElementForLastIndexOf("@");
    };

    private _getPositioningElementForLastIndexOf = (searchString: string): JQuery => {
        const substringLength = this._textControl.value.lastIndexOf(searchString, this._textControl.selectionStart) + 1;
        const replacementString: string = this._getReplacementString(this._textControl.value, 0, substringLength);
        this._textEditorMirrorRef.innerHTML = Utils_String.htmlEncode(replacementString);

        return this._getPositioningElement();
    };

    private _getPositioningElement = (): JQuery => {
        const lineHeight = 20;
        const suggestionDropDownWidth = 600;

        /**
         * If there is a scroll appearing in original text area the width where the text could be rendered is reduced,
         * this should be reflected in mirror span as well to exactly overlap the original text area.
         */
        if (this._textControl.scrollHeight > this._textControl.clientHeight) {
            this._textEditorMirrorContainerRef.style.overflowY = "scroll";
        } else {
            this._textEditorMirrorContainerRef.style.overflowY = "hidden";
        }

        /**
         * Rectangles cordinates are wrt to viewport (not parent element) in px. However, we need to
         * position the dropdown wrt ".markdown-editable" div which contains the textArea.
         * For this we assume the first character in textArea approximately starts at top left corner of textArea.
         * Then all that needs to done is to find the offset position in px of last '#' before caret relative
         * to first character. We need to make sure we adjust for scrolling as well.
         */
        const caretOffset: Point = this._getCursorOffsetRespectToFirstCharacter();

        this._positioningElementRef.style.width = `${suggestionDropDownWidth}px`;
        this._positioningElementRef.style.height = `${lineHeight}px`;
        this._positioningElementRef.style.top = `${caretOffset.y}px`;

        this._positioningElementRef.style.left = `${caretOffset.x}px`;
        this._positioningElementRef.style.right = `auto`;

        this._textEditorMirrorRef.innerHTML = "";
        return $(this._positioningElementRef);
    };

    private _getCursorOffsetRespectToFirstCharacter = (): Point => {
        const mirrorSpanRects = this._textEditorMirrorRef.getClientRects();
        const lastRect: ClientRect = mirrorSpanRects[mirrorSpanRects.length - 1];
        const firstRect: ClientRect = mirrorSpanRects[0];

        const topPadding: string = window.getComputedStyle(this._textControl, null).getPropertyValue("padding-top");
        const caretOffsetY: number = lastRect.top - firstRect.top - this._textControl.scrollTop + Number(topPadding.replace("px", ""));
        const caretOffsetX: number = lastRect.left - firstRect.left - this._textControl.scrollLeft + lastRect.width;

        return { x: caretOffsetX, y: caretOffsetY };
    };

    private _getReplacementString = (originalString: string, start: number, end: number): string => {
        return originalString.substring(start, end).replace(/\n$/, "\n\001");
    };
}

export function translateDisplayNamesToStorageKeysOfPersonMentions(content: string): string {
    return MentionProcessor.getDefault().translateDisplayNamesToStorageKeysOfPersonMentions(content);
}

export function translateStorageKeysToDisplayNamesOfPersonMentions(content: string): IPromise<string> {
    return MentionProcessor.getDefault().translateStorageKeysToDisplayNamesOfPersonMentions(content);
}
